/* ──────────────────────────────────────────────────────────────
   Video Stories — CSS scroll-snap row + fullscreen player.
   - Grid previews autoplay muted and loop their first N seconds.
   - Off-screen previews are paused (IntersectionObserver) for perf.
   - Prev / next arrows scroll the track by one card.
   - Clicking a card opens a fullscreen player that plays from 0:00
     with sound; mute/unmute (bottom-right) and close (top-right).
   - Closing stops the video and resets it to 0:00.
   ────────────────────────────────────────────────────────────── */
(function () {
  function initSection(section) {
    if (section.dataset.vsInit === 'true') return;
    section.dataset.vsInit = 'true';

    var track = section.querySelector('[data-vs-track]');
    var prevBtn = section.querySelector('[data-vs-prev]');
    var nextBtn = section.querySelector('[data-vs-next]');
    var modal = section.querySelector('[data-vs-modal]');
    var modalVideo = modal ? modal.querySelector('[data-vs-modal-video]') : null;
    var muteBtn = modal ? modal.querySelector('[data-vs-mute]') : null;

    var previews = Array.prototype.slice.call(section.querySelectorAll('[data-vs-preview]'));

    // Loop only the first N seconds of each preview.
    previews.forEach(function (video) {
      var loopSeconds = parseFloat(video.getAttribute('data-vs-loop')) || 0;
      if (loopSeconds > 0) {
        video.addEventListener('timeupdate', function () {
          if (video.currentTime >= loopSeconds) video.currentTime = 0;
        });
      }
    });

    // Play previews only while they are on screen.
    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            var v = entry.target;
            if (entry.isIntersecting) {
              var p = v.play();
              if (p && p.catch) p.catch(function () {});
            } else {
              v.pause();
            }
          });
        },
        { threshold: 0.25 }
      );
      previews.forEach(function (v) { io.observe(v); });
    } else {
      previews.forEach(function (v) {
        var p = v.play();
        if (p && p.catch) p.catch(function () {});
      });
    }

    // ── Arrow scrolling ──
    if (track && (prevBtn || nextBtn)) {
      var scrollByCard = function (dir) {
        var card = track.querySelector('.video-stories__card');
        var gap = parseFloat(getComputedStyle(track).columnGap || getComputedStyle(track).gap) || 0;
        var step = card ? card.getBoundingClientRect().width + gap : track.clientWidth * 0.8;
        track.scrollBy({ left: dir * step, behavior: 'smooth' });
      };

      var updateArrows = function () {
        var maxScroll = track.scrollWidth - track.clientWidth - 1;
        if (prevBtn) prevBtn.disabled = track.scrollLeft <= 0;
        if (nextBtn) nextBtn.disabled = track.scrollLeft >= maxScroll;
      };

      if (prevBtn) prevBtn.addEventListener('click', function () { scrollByCard(-1); });
      if (nextBtn) nextBtn.addEventListener('click', function () { scrollByCard(1); });
      track.addEventListener('scroll', updateArrows, { passive: true });
      window.addEventListener('resize', updateArrows);
      updateArrows();
    }

    if (!modal || !modalVideo) return;

    function setMuted(muted) {
      modalVideo.muted = muted;
      if (muteBtn) {
        muteBtn.setAttribute('data-muted', muted ? 'true' : 'false');
        muteBtn.setAttribute('aria-label', muted ? 'Unmute' : 'Mute');
      }
    }

    function openModal(src) {
      modalVideo.src = src;
      modalVideo.currentTime = 0;
      // Opening is a user gesture, so sound is allowed.
      setMuted(false);
      if (typeof modal.showModal === 'function') modal.showModal();
      else modal.setAttribute('open', '');

      var p = modalVideo.play();
      if (p && p.catch) {
        p.catch(function () {
          // If sound-on autoplay is blocked, start muted so it still plays.
          setMuted(true);
          var p2 = modalVideo.play();
          if (p2 && p2.catch) p2.catch(function () {});
        });
      }
    }

    function closeModal() {
      modalVideo.pause();
      modalVideo.removeAttribute('src');
      modalVideo.load(); // fully detach so it stops buffering
      try { modalVideo.currentTime = 0; } catch (e) {}
      if (modal.open && typeof modal.close === 'function') modal.close();
      else modal.removeAttribute('open');
    }

    // Open the player when a card is tapped.
    section.addEventListener('click', function (e) {
      var trigger = e.target.closest('[data-vs-open]');
      if (!trigger || !section.contains(trigger)) return;
      var src = trigger.getAttribute('data-vs-src');
      if (src) openModal(src);
    });

    modal.addEventListener('click', function (e) {
      if (e.target.closest('[data-vs-close]')) { closeModal(); return; }
      if (e.target === modal) closeModal(); // click on backdrop
    });

    // Esc / programmatic close → make sure the video is stopped.
    modal.addEventListener('close', function () {
      modalVideo.pause();
      modalVideo.removeAttribute('src');
      modalVideo.load();
    });

    if (muteBtn) {
      muteBtn.addEventListener('click', function () {
        setMuted(!modalVideo.muted);
        if (!modalVideo.muted) {
          var p = modalVideo.play();
          if (p && p.catch) p.catch(function () {});
        }
      });
    }
  }

  function initAll(scope) {
    var root = scope && scope.querySelectorAll ? scope : document;
    var sections = root.querySelectorAll('.video-stories[data-vs-section]');
    Array.prototype.forEach.call(sections, initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }

  // Theme editor: re-init when this section is (re)loaded.
  document.addEventListener('shopify:section:load', function (e) {
    initAll(e.target);
  });
})();